/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { AfterContentInit, Component, OnInit, DOCUMENT, inject, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { GTMConfig, NewRelicService, PidxGoogleTagManagerIntegrationService, WINDOW } from '@myprofile-ui/pidx-utils'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { filter } from 'rxjs/operators'
import { ConfigService, HeaderExternalLink } from './services/configuration/config.service'
import { FeatureToggle } from './services/feature-toggles.defintions'
import { FeatureToggleService } from './services/feature-toggle/feature-toggle.service'
import { FullstoryService } from './services/fullstory/fullstory.service'
import { MetaFieldsService } from './services/meta-fields/meta-fields.service'
import { getMetaTitleKeysFromRouterRootSnapshot } from './services/meta-fields/meta-fields.route-utils'

@UntilDestroy()
@Component({
  selector: 'myprofile-layout',
  templateUrl: './layout.component.html',
  host: { class: 'profile:flex profile:flex-col profile:min-h-screen' },
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class LayoutComponent implements OnInit, AfterContentInit {
  private _document = inject<Document>(DOCUMENT)
  private _window = inject<Window>(WINDOW)
  private _configService = inject(ConfigService)
  private _activatedRoute = inject(ActivatedRoute)
  private _googleTagManagerIntegration = inject(PidxGoogleTagManagerIntegrationService)
  private _router = inject(Router)
  private _featureToggleService = inject(FeatureToggleService)
  private newRelicService = inject(NewRelicService)

  #fullStoryService = inject(FullstoryService)
  #metaFieldsService = inject(MetaFieldsService)

  public navigationItems: HeaderExternalLink[] = []
  public currentLanguage!: string
  public currentCountry!: string
  public activeMenuItem!: string
  public isProd = false
  public locale = 'en-GB'

  private rtlLanguages = ['ar_AE']

  constructor() {
    this.navigationItems = this._configService.getHeaderLinks()
  }

  public ngOnInit() {
    this.currentCountry = this._configService.getUrlCountry()
    this.currentLanguage = this._configService.getUrlLocale()
    this.locale = this.parseLocale()
    this.isProd = this._configService.isProduction()

    this.setDirection()
    this.activeMenuItem = this._activatedRoute?.firstChild?.firstChild?.firstChild?.firstChild?.snapshot?.data?.activeMenuItem

    const syncDocumentTitleFromRouter = () => {
      const metaTitleKeys = getMetaTitleKeysFromRouterRootSnapshot(this._router.routerState.snapshot.root)
      this.#metaFieldsService.updateTitle(metaTitleKeys ?? [])
    }

    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.activeMenuItem = this._activatedRoute?.firstChild?.firstChild?.firstChild?.firstChild?.snapshot?.data?.activeMenuItem
        syncDocumentTitleFromRouter()
        this._googleTagManagerIntegration.triggerPageLoad()
      })

    syncDocumentTitleFromRouter()

    this.newRelicService.addPageLoadAction(this.currentCountry)
    this._setupNavigation(this.navigationItems)
  }

  private setDirection() {
    const direction = this.rtlLanguages.includes(this.currentLanguage) ? 'rtl' : 'ltr'
    this._document.documentElement.setAttribute('dir', direction)
  }

  public languageChanged(selectedLocale: string) {
    // This will get the url-path after the country/locales like 'success-page'
    const routeSegmentsWithoutCountryAndLocale = this._router.url.split('/').slice(3)
    this._document.location.href = [
      this._configService.getAppBaseUrl(),
      this._configService.getUrlCountry(),
      selectedLocale,
      ...routeSegmentsWithoutCountryAndLocale,
    ].join('/')
  }

  private _setupTracking() {
    const trackingConfig: GTMConfig = {
      country: this.currentCountry,
      language: this.currentLanguage,
      environment: this._configService.getGTMEnvironment(),
      applicationId: this._configService.getGTMApplicationID(),
      server: new URL(this._configService.getBaseUrl()).hostname,
    }
    this._googleTagManagerIntegration.init(trackingConfig, this._configService.getGTMIsEnabled(), this._configService.getGTMContainerID())
    this._googleTagManagerIntegration.triggerPageLoad()
  }

  private _setupNavigation(headerLinks: HeaderExternalLink[]) {
    this.navigationItems = headerLinks
    if (this._featureToggleService.isFeatureEnabled(FeatureToggle.HIDE_PAYMENT_AND_ORDER_SLICE)) {
      this.navigationItems = this.navigationItems.filter((value) => value.key !== 'MENU_ITEM_PAYMENTMETHODS')
    }
    if (this._featureToggleService.isFeatureEnabled(FeatureToggle.HIDE_SECONDARY_USER_SLICE)) {
      this.navigationItems = this.navigationItems.filter((value) => value.key !== 'MENU_ITEM_SECONDARY_USER')
    }
    const dummyNavElement = this._document.getElementById('dummyNav')
    if (dummyNavElement) {
      this._document.body.removeChild(dummyNavElement)
    }
  }

  getActiveTab() {
    let returnedIndex = 0
    this.navigationItems.find((value, index) => {
      if (value.key === this.activeMenuItem) {
        returnedIndex = index
      }
      return value.key === this.activeMenuItem
    })

    return returnedIndex
  }

  goToUrl(url: string, event?: Event) {
    event?.preventDefault()
    const parts = url.split('/')
    const route = parts.pop()
    if (route === 'login-and-security') {
      this._window.location.href = url
    } else {
      this._router.navigate([this._configService.getUrlCountry(), this._configService.getUrlLocale(), route], {
        relativeTo: this._activatedRoute,
      })
    }
  }

  checkShowPageTitle() {
    const urlParts: string[] = this._window.location.href.split('/')
    const lastPart = urlParts.pop()
    return lastPart !== 'add' && lastPart !== 'activate'
  }

  public ngAfterContentInit() {
    this._setupTracking()
    this.#fullStoryService.setupFullstory()
  }

  parseLocale(): string {
    const urlLocale = this._configService.getUrlLocale()
    const urlCountry = this._configService.getUrlCountry()
    if (urlLocale && urlCountry) {
      // Special handling for the UAE, as Navigation has different locales for Arabic and English
      if (urlCountry === 'xa') {
        return urlLocale === 'en_GB' ? 'en-AE-x-abu-dhabi' : 'ar-AE-x-abu-dhabi'
      } else if (urlCountry === 'xd') {
        return urlLocale === 'en_GB' ? 'en-AE-x-dubai' : 'ar-AE-x-dubai'
      }
      return `${urlLocale.substring(0, 2).toLowerCase()}-${urlCountry.toUpperCase()}`
    }
    return ''
  }
}
